import { BooleanFieldModel, StringFieldModel } from "@/common/commonForms";
import { makeAutoObservable } from "mobx";
import { Language } from "@/i18n/languages";
import {
  ExtendedShareholderDTO,
  PersonInfo,
  ShareholderDetailsResponseDTO,
} from "@/types/Shareholder";
import { formatOrgNum } from "@/utils/other";
import { formatDate } from "@/i18n/dateFormat";
import { TMessage } from "@/i18n/tt";
import { norway } from "@/utils/countries";
import { idnr } from "@navikt/fnrvalidator";
import { logDebug } from "@/utils/log";
import { SelectProps } from "@/components-v2/Select";
import { identity } from "lodash";
import { ShareTypeCode } from "@/common/share";
import { filterShareholdersByStatus, isOptionHolder } from "@/stores/ShareholdersStore";

export class ShareholderPledgeForm {
  mode: "view" | "edit" | "saving" = "view";
  isPledged: BooleanFieldModel = new BooleanFieldModel();
  comment: StringFieldModel = new StringFieldModel();
  error: unknown;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setMode(mode: "view" | "edit" | "saving") {
    this.mode = mode;
  }

  setIsPledged(isPledged: boolean) {
    this.isPledged.setValue(isPledged);
  }

  setPledgeComment(comment: string) {
    this.comment.setInputValue(comment);
  }

  toData() {
    return {
      isPledged: this.isPledged.value,
      pledgeComment: this.comment.validatedValue,
    };
  }

  setError(error: unknown) {
    this.error = error;
  }

  resetError() {
    this.error = undefined;
  }

  resetForm() {
    this.resetError();
    this.setMode("view");
    this.isPledged.setValue("");
    this.comment.setInputValue("");
  }
}

export class ShareholderRemarksForm {
  mode: "hide" | "edit" | "saving" = "hide";
  remark: StringFieldModel = new StringFieldModel();
  shareholderName?: string;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  openDialog(remark?: string) {
    this.setRemark(remark);
    this.setMode("edit");
  }

  setMode(mode: "hide" | "edit" | "saving") {
    this.mode = mode;
  }

  setRemark(remark?: string) {
    this.remark.setAndCommitInputValue(remark ?? "");
  }

  setShareholderName(name: string) {
    this.shareholderName = name;
  }

  get remarkValue() {
    return this.remark.isEmpty ? undefined : this.remark.validatedValue;
  }

  toData() {
    return {
      remark: this.remarkValue,
    };
  }

  validate() {
    this.remark.validate();
  }

  get isValid() {
    return this.remark.isValid;
  }
}

export const formatBirthDateOrOrganisationNumber = (
  locale: Language,
  shareholder?: ExtendedShareholderDTO | ShareholderDetailsResponseDTO,
): string => {
  if (!shareholder) {
    return "";
  }
  if (shareholder.isCompany) {
    return formatOrgNum(shareholder.birthDateOrOrganisationNumber);
  }
  return formatDate(locale, shareholder.birthDateOrOrganisationNumber);
};
export const isValidPersonalId = (value: string) => {
  return /^\d{11}$/.test(value);
};
const isPersonalIdChanged = (value: string, existingPersonInfo: PersonInfo): boolean =>
  value !== (existingPersonInfo.socialSecurityNumber ?? "");

export function validateSocialSecurityNumberWithCountryCode(
  value: string,
  countryCode: string,
  existingPersonInfo: PersonInfo | undefined,
): TMessage | undefined {
  if (existingPersonInfo && !isPersonalIdChanged(value, existingPersonInfo)) {
    return undefined;
  } else if (norway === countryCode) {
    if (!value) {
      return { key: "newShareholder.socialSecurityNumber.required" };
    } else {
      const validationResult: OkResult | ErrorResult = idnr(value);
      if (validationResult.status === "valid") {
        return undefined;
      } else {
        return { key: "newShareholder.personal_id.validation-error" };
      }
    }
  }
}

export function getShareSumByType(shareholders: ExtendedShareholderDTO[]): Record<string, number> {
  return shareholders.reduce<{ [shareType: string]: number }>((sum, holder) => {
    Object.entries(holder.calculatedShares).forEach(
      ([key, shares]) => (sum[key] = (sum[key] || 0) + shares),
    );
    return sum;
  }, {});
}

export function getShareSumWithoutCompany(
  orgNo: string,
  shareholders: ExtendedShareholderDTO[],
  shareSumByType: Record<string, number>,
): Record<string, number> {
  const result = { ...shareSumByType };

  const ownCompanyAsShareholder = shareholders.find(
    (s) => s.birthDateOrOrganisationNumber === orgNo,
  );

  if (ownCompanyAsShareholder) {
    Object.entries(result).forEach(([type]) => {
      logDebug("TODO::::check the share type code ++");
      result[type] -= ownCompanyAsShareholder.calculatedShares[type as ShareTypeCode] || 0;
    });
  }
  return result;
}

export type SharenumberInterval = {
  sharenumberFromInclusive: number;
  sharenumberToInclusive: number;
};

export function calculateNumberOfShares(interval: SharenumberInterval): number {
  return interval.sharenumberToInclusive - interval.sharenumberFromInclusive + 1;
}

export function calculateTotalNumberOfShares(intervals: SharenumberInterval[]): number {
  return intervals.reduce((sum, interval) => sum + calculateNumberOfShares(interval), 0);
}

export type ShareholderContribution = {
  shareholderContributionId: number;
  organisationNumber: string;
  type: ShareholderContributionType;
  registrationDate: string;
  comment: string | null;
  details: ShareholderContributionDetails[];
  createdAt?: string;
  updatedAt?: string;
  reference?: string;
  registeredBy?: number;
};
export type ShareholderContributionDetails = {
  shareholderContributionDetailsId: number;
  shareholderContributionId: number;
  shareholderId: number;
  currency: string;
  recallableAmount: number;
  amount: number;
};
export const shareholderContributionTypes = ["COMMITMENT", "CONTRIBUTION", "DISTRIBUTION"] as const;
export type ShareholderContributionType = (typeof shareholderContributionTypes)[number];

export type ShareholderCommitment = {
  shareholderCommitmentId: string;
  organisationNumber: string;
  type: ShareholderCommitmentType;
  registrationDate: string;
  comment: string | null;
  details: ShareholderCommitmentDetails[];
  createdAt: string;
  updatedAt: string;
  registeredBy: number;
};
export type ShareholderCommitmentDetails = {
  shareholderCommitmentDetailsId: string;
  shareholderCommitmentId: string;
  shareholderId: number;
  currency: string;
  recallableAmount?: number;
  amount: number;
};
export type CreateShareholderCommitmentRequest = {
  organisationNumber: string;
  type: ShareholderCommitmentType;
  registrationDate: string;
  comment: string | null;
  details: CreateShareholderCommitmentDetailsRequest[];
};
export type CreateShareholderCommitmentDetailsRequest = {
  shareholderId: number;
  currency: string;
  recallableAmount?: number;
  amount: number;
};
export const shareholderCommitmentTypes = ["COMMITMENT", "DRAW", "DISTRIBUTION"] as const;
export type ShareholderCommitmentType = (typeof shareholderCommitmentTypes)[number];

export const shareholderStatuses = ["all", "current", "former", "option-holders"] as const;
export type ShareholderStatus = (typeof shareholderStatuses)[number];

export function shareholderStatusLabel(status: ShareholderStatus, count: number): TMessage {
  switch (status) {
    case "all":
      return {
        key: "shareholders.showActiveShareholders.all",
        params: {
          count,
        },
      };
    case "current":
      return { key: "shareholders.showActiveShareholders.current", params: { count } };
    case "former":
      return { key: "shareholders.showActiveShareholders.former", params: { count } };
    case "option-holders":
      return { key: "shareholders.showActiveShareholders.option-holders", params: { count } };
  }
}

export const prepShareholderStatusFilter = (
  value: ShareholderStatus | undefined,
  onSelect: (value: ShareholderStatus) => void,
  shareholders: ExtendedShareholderDTO[],
): SelectProps<ShareholderStatus> => {
  return {
    value: value ?? "current",
    onSelect,
    items: shareholderStatuses.filter(
      (option) => option !== "option-holders" || shareholders.some(isOptionHolder),
    ),
    idFn: identity,
    labelFn: (option) =>
      shareholderStatusLabel(option, filterShareholdersByStatus(shareholders, option).length),
    iconFn: (option) => {
      switch (option) {
        case "current":
          return { name: "circle", color: "success", weight: "fill", size: "xs" };
        case "former":
          return { name: "circle", color: "subtle", size: "xs" };
        case "option-holders":
          return "seal-check";
        case "all":
          return "notebook";
      }
    },
  };
};
